import React, { useMemo, useState, useCallback, useEffect } from "react";
import Table from "../../../../../../components/TableV2";
import TitleButtons from "../../../../../../components/TitleButtons";
import LeftTable from "../../../../../../components/LeftTable";
import { useStandbyComercial } from "./standbyData-comercial";
import Modal from "../../../../../../components/Modal";
import { generatePdfReport } from "../../../../../../utils/pdf/Report-Standby-Technical";
import { generatePdfReportAllOS } from "../../../../../../utils/pdf/Report-Standby-All-OS";

type Row = (string | number)[];

const IDX = {
  PV: 0,
  TIPO: 1,
  CODIGO: 2,
  NOME: 3,
  QTD: 4,
  CUSTO: 5,
  TECNICO: 6, // "R. retirada"
  DATA: 7,
  HORA: 8,
};

type RawProduto = {
  id?: number;
  codigo: string;
  nome: string;
  posicao?: string | null;
};

type RawItem = {
  id: number;
  movimentacao_id: number; // origem (standby-venda)
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  moeda?: string | null;
  produto: RawProduto;
};

type RawMov = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null;
    departamento_origem: string | null;
    tipo: string | null;
    proposta_venda?: string | null;
    os_numero?: string | null;
    coletor_nome?: string | null;
    criado_em: string;
    embarcacao?: string | null;
    empresa_cliente?: string | null;
  };
  itens: RawItem[];
};

const API_BASE = "https://api.eva-googlemarine.com";
const URL_LIST = `${API_BASE}/departamentos/comercial/standby`;
const URL_RETORNAR = `${API_BASE}/movimentar-vendas-retornar`; // ✅ endpoint correto (venda)
const URL_BAIXAR = `${API_BASE}/movimentar-vendas-baixar`; // ajuste se necessário

// substitui a antiga parseBRL
function parseBRL(s: unknown): number {
  if (typeof s === "number") return s;

  const raw = String(s ?? "").trim();
  if (!raw) return 0;

  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  // Caso típico de API: "106.00", "1609.77" → ponto como decimal
  if (hasDot && !hasComma) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  // Caso típico BR: "1.234,56", "1.234" etc.
  const cleaned = raw
    .replace(/[^\d,.-]/g, "") // tira R$, espaços etc.
    .replace(/\./g, "") // remove separador de milhar
    .replace(",", "."); // vírgula vira decimal

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function normPV(v: unknown): string {
  return String(v ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

// símbolo por código de moeda
function getCurrencySymbol(code: string | null | undefined): string {
  const c = String(code || "BRL").toUpperCase();
  switch (c) {
    case "BRL":
      return "R$";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    default:
      // se for algo tipo "CAD", "JPY", etc.
      return c;
  }
}

const SuprimentosStandby: React.FC = () => {
  const DEV_MODE = false;

  const headers = useMemo(
    () => [
      "PV",
      "Tipo",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "Moeda", // 👈 NOVA COLUNA
      "R. retirada",
      "Data",
      "Hora",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "60px", // PV
      "80px", // Tipo
      "70px", // Código
      "300px", // Nome
      "10px", // Quantidade
      "110px", // Custo Unitário
      "70px", // Moeda
      "120px", // R. retirada
      "80px", // Data
      "60px", // Hora
    ],
    []
  );

  const { rows, loading, refresh, rawByProposta } =
    useStandbyComercial() as any;

  const displayRows: Row[] = useMemo(() => {
    if (!rows?.length) return rows || [];

    return (rows as Row[]).map((r) => {
      const pv = String(r[IDX.PV] ?? "-");
      const rawEntry = rawByProposta?.[pv];

      // responsável / coletor
      const respNome =
        rawEntry?.mov?.responsavel_nome ||
        rawEntry?.mov?.coletor_nome ||
        String(r[IDX.TECNICO] ?? "-");

      // código do produto da linha UI
      const codigoRow = String(r[IDX.CODIGO] ?? "")
        .trim()
        .toUpperCase();

      // tenta achar o item RAW correspondente a esse código
      let moedaCode = "BRL";
      let custoFonte: unknown = r[IDX.CUSTO];

      if (rawEntry?.itens && Array.isArray(rawEntry.itens)) {
        const match = rawEntry.itens.find((it: RawItem) => {
          const cod = String(it.produto?.codigo ?? "")
            .trim()
            .toUpperCase();
          return cod === codigoRow;
        });

        if (match) {
          moedaCode = String(match.moeda || "BRL").toUpperCase();
          // se vier custo_unitario da API, usa ele como fonte
          if (match.custo_unitario != null) {
            custoFonte = match.custo_unitario;
          }
        }
      }

      const symbol = getCurrencySymbol(moedaCode);
      const custoNum = parseBRL(custoFonte);

      let custoFormatado: string;
      if (Number.isFinite(custoNum) && custoNum > 0) {
        custoFormatado = `${symbol} ${custoNum.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      } else {
        custoFormatado = String(custoFonte ?? "-");
      }

      const newRow: Row = [
        r[IDX.PV],
        r[IDX.TIPO],
        r[IDX.CODIGO],
        r[IDX.NOME],
        r[IDX.QTD],
        custoFormatado, // custo com símbolo certo para aquele item
        moedaCode, // coluna "Moeda" por item
        respNome || "-",
        r[IDX.DATA],
        r[IDX.HORA],
      ];

      return newRow;
    });
  }, [rows, rawByProposta]);

  const [openPV, setopenPV] = useState<string | null>(null);
  const closeModal = useCallback(() => setopenPV(null), []);
  const handleDoubleClick = useCallback(
    (rowIndex: number) => {
      const r = displayRows[rowIndex] as Row | undefined;
      if (!r) return;
      const pv = String(r[IDX.PV] ?? "").trim();
      if (!pv) return;
      setopenPV(pv);
    },
    [displayRows]
  );

  // ---------- RAW da PV ----------
  const [rawMovs, setRawMovs] = useState<RawMov[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadRaw() {
      if (!openPV) return;
      setBusy(false);
      setSelectedIndex(null);
      try {
        const res = await fetch(URL_LIST, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = Array.isArray(json?.data) ? (json.data as RawMov[]) : [];

        const pvNorm = normPV(openPV);
        // Filtro cobre PV (proposta_venda) e OS (os_numero)
        const onlyThisPV = data.filter((m) => {
          const chave =
            m?.movimentacao?.proposta_venda || m?.movimentacao?.os_numero;
          return normPV(chave) === pvNorm;
        });

        if (!cancelled) setRawMovs(onlyThisPV);
      } catch (e) {
        console.error("Falha ao carregar detalhes RAW:", e);
        if (!cancelled) setRawMovs(null);
      }
    }
    loadRaw();
    return () => {
      cancelled = true;
    };
  }, [openPV]);

  // Embarcação (UI)
  const embarcacaoNome = useMemo(() => {
    if (!rawMovs?.length) return "-";
    const mv = rawMovs.find((m) => (m.movimentacao.embarcacao || "").trim());
    return (mv?.movimentacao.embarcacao || "-").trim();
  }, [rawMovs]);

  // ---------- Detalhes da PV (UI) ----------
  const pvDetails = useMemo(() => {
    if (!openPV) return null;
    const pvNorm = normPV(openPV);

    // usa as linhas originais (rows) para manter os índices do IDX
    const itens = (rows as Row[]).filter((r) => normPV(r[IDX.PV]) === pvNorm);
    if (!itens.length)
      return {
        info: [] as { label: string; value: string }[],
        headers: [] as string[],
        tableRows: [] as Row[],
        colWidths: [] as string[],
        totalMateriais: 0,
        totalUnidades: 0,
        totalCusto: 0,
      };

    const first = itens[0];
    const data = String(first[IDX.DATA] ?? "-");
    const hora = String(first[IDX.HORA] ?? "-");

    const rawEntry = rawByProposta?.[String(openPV)];
    const responsavelRetirada =
      rawEntry?.mov?.responsavel_nome ||
      rawEntry?.mov?.coletor_nome ||
      String(first[IDX.TECNICO] ?? "-");

    const totalUnidades = itens.reduce(
      (acc, r) => acc + Number(r[IDX.QTD] ?? 0),
      0
    );
    const totalMateriais = itens.length;
    const totalCusto = itens.reduce(
      (acc, r) => acc + parseBRL(r[IDX.CUSTO]) * Number(r[IDX.QTD] ?? 0),
      0
    );

    const info = [
      { label: "Número da P.V.", value: openPV },
      { label: "R. retirada", value: responsavelRetirada || "-" },
      { label: "Embarcação", value: embarcacaoNome || "-" },
      { label: "Data", value: data },
      { label: "Hora", value: hora },
      {
        label: "Total de Materiais Coletados",
        value: `${totalMateriais} itens, ${totalUnidades} unidades`,
      },
      {
        label: "Custo Total (aprox.)",
        value:
          isFinite(totalCusto) && totalCusto > 0
            ? totalCusto.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL", // aqui continua BRL (se quiser, depois a gente pode sofisticar)
              })
            : "-",
      },
    ];

    const detailsHeaders = [
      "Tipo",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "R. retirada",
      "Data",
      "Hora",
    ];
    const detailsColWidths = [
      "80px",
      "100px",
      "350px",
      "90px",
      "180px",
      "90px",
      "70px",
    ];

    const tableRows = itens.map((r) => [
      String(r[IDX.TIPO] ?? "-"),
      String(r[IDX.CODIGO] ?? "-"),
      String(r[IDX.NOME] ?? "-"),
      String(r[IDX.QTD] ?? "-"),
      String(responsavelRetirada || "-"),
      String(r[IDX.DATA] ?? "-"),
      String(r[IDX.HORA] ?? "-"),
    ]);

    return {
      info,
      headers: detailsHeaders,
      colWidths: detailsColWidths,
      tableRows,
      totalMateriais,
      totalUnidades,
      totalCusto,
    };
  }, [openPV, rows, embarcacaoNome, rawByProposta]);

  // ---------- Mapeia linhas UI → itens RAW ----------
  const rawItemsForUI: (RawItem | null)[] = useMemo(() => {
    if (!openPV || !pvDetails || !rawMovs) return [];
    const byCode = new Map<string, RawItem[]>();

    const pool: RawItem[] = rawMovs.flatMap((m) => m.itens || []);
    for (const it of pool) {
      const code = (it.produto?.codigo ?? "").trim().toUpperCase();
      if (!byCode.has(code)) byCode.set(code, []);
      byCode.get(code)!.push(it);
    }

    byCode.forEach((arr: RawItem[]) => {
      arr.sort((a: RawItem, b: RawItem) => {
        const am = Number(a?.movimentacao_id ?? 0);
        const bm = Number(b?.movimentacao_id ?? 0);
        return am - bm;
      });
    });

    return pvDetails.tableRows.map((uiRow) => {
      const code = String(uiRow[1] ?? "")
        .trim()
        .toUpperCase();
      const queue = byCode.get(code);
      if (!queue || queue.length === 0) return null;
      return queue.shift() ?? null;
    });
  }, [openPV, pvDetails, rawMovs]);

  function findRawItemByCode(codeUI: string): RawItem | null {
    if (!rawMovs?.length) return null;
    const code = (codeUI ?? "").trim().toUpperCase();
    for (const bucket of rawMovs) {
      for (const it of bucket.itens || []) {
        if ((it.produto?.codigo ?? "").trim().toUpperCase() === code) {
          return it;
        }
      }
    }
    return null;
  }

  const responsavelId = useMemo(() => {
    if (!openPV) return null;
    const fromMap = rawByProposta?.[String(openPV)]?.mov?.responsavel_id;
    const n1 = Number(fromMap);
    if (Number.isFinite(n1)) return Math.trunc(n1);
    if (!rawMovs?.length) return null;
    const withResp = rawMovs.find((m) => m.movimentacao.responsavel_id != null);
    const n2 = Number(withResp?.movimentacao.responsavel_id);
    return Number.isFinite(n2) ? Math.trunc(n2) : null;
  }, [openPV, rawByProposta, rawMovs]);

  async function postJSON(url: string, payload: any) {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} – ${t || "erro ao enviar"}`);
    }
    return res.json().catch(() => ({}));
  }

  async function askQuantidade(max: number): Promise<number | null> {
    const val = window.prompt(
      `Quantidade (máxima: ${max})`,
      String(Math.min(1, max))
    );
    if (val == null) return null;
    const n = Number(String(val).replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      window.alert("Informe um número válido maior que zero.");
      return null;
    }
    if (!Number.isInteger(n)) {
      window.alert("A quantidade deve ser inteira.");
      return null;
    }
    if (n > max) {
      window.alert(`A quantidade não pode exceder ${max}.`);
      return null;
    }
    return n;
  }

  // ======== RELATÓRIOS ========

  const handleGerarRelatorioDaOs = useCallback(async () => {
    if (!openPV || !pvDetails) {
      return window.alert("Abra uma P.V. para gerar o relatório completo.");
    }
    if (!rawMovs || !rawMovs.length) {
      return window.alert("Dados RAW da P.V. indisponíveis.");
    }

    const allItems = rawMovs.flatMap((m) =>
      (m.itens || []).map((it) => ({
        codigo: it.produto?.codigo || "-",
        nome: it.produto?.nome || "-",
        quantidade: Number(it.quantidade ?? 0),
        custoUnitario: (() => {
          const rawStr = it.custo_unitario;
          if (rawStr == null) return null;
          const n = Number(String(rawStr).replace(",", "."));
          return Number.isFinite(n) ? n : null;
        })(),
        tecnico:
          m.movimentacao.responsavel_nome || m.movimentacao.coletor_nome || "-",
      }))
    );

    const tecnicosSet = new Set<string>();
    rawMovs.forEach((m) => {
      const t = (
        m.movimentacao.responsavel_nome ||
        m.movimentacao.coletor_nome ||
        ""
      ).trim();
      if (t) tecnicosSet.add(t);
    });
    const tecnicos = Array.from(tecnicosSet);

    try {
      await generatePdfReportAllOS({
        osNumero: openPV,
        tecnicos,
        materiais: allItems,
      });
    } catch (e: any) {
      window.alert(
        `Falha ao gerar o relatório completo da P.V.: ${e?.message || e}`
      );
    }
  }, [openPV, pvDetails, rawMovs]);

  const handleGerarRelatorio = useCallback(async () => {
    if (!openPV || !pvDetails) {
      return window.alert(
        "Abra uma P.V. e selecione um item para gerar o relatório."
      );
    }
    if (selectedIndex == null) {
      return window.alert(
        "Selecione um material na lista para gerar o relatório."
      );
    }
    if (!rawMovs || !rawMovs.length) {
      return window.alert("Dados RAW da P.V. indisponíveis.");
    }

    const responsavelNome =
      rawMovs.find((m) => m.movimentacao.responsavel_nome)?.movimentacao
        .responsavel_nome ||
      rawMovs.find((m) => m.movimentacao.coletor_nome)?.movimentacao
        .coletor_nome ||
      (pvDetails.tableRows[selectedIndex]?.[4] as string) ||
      "-";

    const ui = pvDetails.tableRows[selectedIndex];
    if (!ui) return window.alert("Item selecionado não encontrado.");

    const codigo = String(ui[1] ?? "-");
    const nome = String(ui[2] ?? "-");
    const quantidade = Number(ui[3] ?? 0);

    try {
      await generatePdfReport({
        osNumero: openPV,
        tecnicoNome: responsavelNome,
        materiais: [{ codigo, nome, quantidade }],
      });
    } catch (e: any) {
      window.alert(`Falha ao gerar o relatório: ${e?.message || e}`);
    }
  }, [openPV, pvDetails, selectedIndex, rawMovs]);

  // ======== AÇÕES (RETORNAR / BAIXAR) — proposta_venda + movimentacao_id =========

  const handleRetornarItem = useCallback(async () => {
    if (!openPV || !pvDetails) return;
    if (selectedIndex == null)
      return window.alert("Selecione um material na lista.");
    if (rawMovs == null || responsavelId == null)
      return window.alert("Dados da P.V. indisponíveis (sem responsável).");

    // Mapeado → fallback por código, se necessário
    let rawItem = rawItemsForUI[selectedIndex];
    if (!rawItem) {
      const ui = pvDetails.tableRows[selectedIndex];
      const codeUI = String(ui?.[1] ?? "");
      rawItem = findRawItemByCode(codeUI);
    }
    if (!rawItem)
      return window.alert("Não foi possível localizar o item selecionado.");

    const max = Number(rawItem.quantidade ?? 0);
    if (!(max > 0))
      return window.alert("Item sem quantidade disponível para retorno.");

    const qtd = await askQuantidade(max);
    if (qtd == null) return;

    const payload = {
      responsavel_id: responsavelId,
      proposta_venda: openPV,
      itens: [
        {
          movimentacao_id: rawItem.movimentacao_id, // ✅ chave correta
          produto_id: rawItem.produto_id,
          quantidade: qtd,
        },
      ],
    };

    if (DEV_MODE) {
      alert("📦 Retornar ITEM payload:\n\n" + JSON.stringify(payload, null, 2));
      return;
    }

    try {
      setBusy(true);
      await postJSON(URL_RETORNAR, payload);
      window.alert("Retorno da venda realizado com sucesso.");
      await refresh?.();
      setopenPV(null);
    } catch (e: any) {
      window.alert(`Falha ao retornar item: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [
    openPV,
    pvDetails,
    selectedIndex,
    rawItemsForUI,
    responsavelId,
    refresh,
    rawMovs,
  ]);

  const handleBaixarItem = useCallback(async () => {
    if (!openPV || !pvDetails) return;
    if (selectedIndex == null)
      return window.alert("Selecione um material na lista.");
    if (rawMovs == null || responsavelId == null)
      return window.alert("Dados da P.V. indisponíveis (sem responsável).");

    // Mapeado → fallback por código, se necessário
    let rawItem = rawItemsForUI[selectedIndex];
    if (!rawItem) {
      const ui = pvDetails.tableRows[selectedIndex];
      const codeUI = String(ui?.[1] ?? "");
      rawItem = findRawItemByCode(codeUI);
    }
    if (!rawItem)
      return window.alert("Não foi possível localizar o item selecionado.");

    const max = Number(rawItem.quantidade ?? 0);
    if (!(max > 0))
      return window.alert("Item sem quantidade disponível para baixa.");

    const qtd = await askQuantidade(max);
    if (qtd == null) return;

    const payload = {
      responsavel_id: responsavelId,
      proposta_venda: openPV,
      itens: [
        {
          origem_mov_id: rawItem.movimentacao_id, // <- backend de "baixar" aceita origem_mov_id
          produto_id: rawItem.produto_id,
          quantidade: qtd,
        },
      ],
    };

    if (DEV_MODE) {
      alert("📦 Baixar ITEM payload:\n\n" + JSON.stringify(payload, null, 2));
      return;
    }

    try {
      setBusy(true);
      await postJSON(URL_BAIXAR, payload);
      window.alert("Baixa realizada com sucesso.");
      await refresh?.();
      setopenPV(null);
    } catch (e: any) {
      window.alert(`Falha ao dar baixa no item: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [
    openPV,
    pvDetails,
    selectedIndex,
    rawItemsForUI,
    responsavelId,
    refresh,
    rawMovs,
  ]);

  const handleRetornarOSInteira = useCallback(async () => {
    if (!openPV || rawMovs == null) return;
    if (responsavelId == null)
      return window.alert("Sem responsável definido para esta P.V.");

    const itens = rawMovs
      .flatMap((m) => m.itens || [])
      .filter((it) => Number(it.quantidade) > 0)
      .map((it) => ({
        movimentacao_id: it.movimentacao_id, // ✅ chave correta
        produto_id: it.produto_id,
        quantidade: Number(it.quantidade),
      }));

    if (!itens.length)
      return window.alert("Não há itens com quantidade para retornar.");

    const payload = {
      responsavel_id: responsavelId,
      proposta_venda: openPV,
      itens,
    };

    if (DEV_MODE) {
      alert("📦 Retornar P.V. payload:\n\n" + JSON.stringify(payload, null, 2));
      return;
    }

    try {
      setBusy(true);
      await postJSON(URL_RETORNAR, payload);
      window.alert("Todos os itens da P.V. foram retornados ao estoque.");
      await refresh?.();
      setopenPV(null);
    } catch (e: any) {
      window.alert(`Falha ao retornar P.V.: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [openPV, rawMovs, responsavelId, refresh]);

  const handleBaixarOSInteira = useCallback(async () => {
    if (!openPV || rawMovs == null) return;
    if (responsavelId == null)
      return window.alert("Sem responsável definido para esta P.V.");

    const itens = rawMovs
      .flatMap((m) => m.itens || [])
      .filter((it) => Number(it.quantidade) > 0)
      .map((it) => ({
        origem_mov_id: it.movimentacao_id, // backend de "baixar" usa origem_mov_id
        produto_id: it.produto_id,
        quantidade: Number(it.quantidade),
      }));

    if (!itens.length)
      return window.alert("Não há itens com quantidade para dar baixa.");

    const payload = {
      responsavel_id: responsavelId,
      proposta_venda: openPV,
      itens,
    };

    if (DEV_MODE) {
      alert("📦 Baixar P.V. payload:\n\n" + JSON.stringify(payload, null, 2));
      return;
    }

    try {
      setBusy(true);
      await postJSON(URL_BAIXAR, payload);
      window.alert("Todos os itens da P.V. tiveram baixa realizada.");
      await refresh?.();
      setopenPV(null);
    } catch (e: any) {
      window.alert(`Falha ao dar baixa na P.V.: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [openPV, rawMovs, responsavelId, refresh]);

  const handleModalClose = useCallback(() => {
    if (busy) return;
    closeModal();
  }, [busy, closeModal]);

  return (
    <div>
      <TitleButtons
        fullBleedLine
        title="Suprimentos - Standby Comercial - Estoque de Serviço & Comercial"
        buttons={
          [
            // exemplo de botão futuro
            // {
            //   label: loading ? "Carregando..." : "Gerar Relatório",
            //   onClick: handleGerarRelatorioDaOs,
            //   variant: "primary",
            // },
          ]
        }
      />

      <Table
        headers={headers}
        rows={displayRows}
        columnWidths={columnWidths}
        isTableStandby
        onDoubleClick={handleDoubleClick}
        responsiveWidths={{
          1300: {
            1: "60px", // Tipo
            2: "0px", // Código (esconde)
            3: "300px", // Nome
            4: "0px", // Quantidade (esconde)
            5: "100px", // Custo Unitário
            6: "60px", // Moeda
            7: "100px", // R. retirada
            8: "60px", // Data
            9: "60px", // Hora
          },
        }}
      />

      <Modal open={!!openPV} onClose={handleModalClose} maxWidth={1280}>
        {openPV && pvDetails && (
          <div
            style={{
              padding: 0,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 8,
              width: "100%",
            }}
          >
            <div style={{ width: "100%" }}>
              <LeftTable data={pvDetails.info} />
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              <button
                disabled={busy || rawMovs == null || responsavelId == null}
                onClick={handleRetornarOSInteira}
                style={{
                  marginBottom: "8px",
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #0a7",
                  background: "#f0fffa",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Retorna todos os itens desta P.V. ao estoque"
              >
                Retornar P.V. ao estoque
              </button>
              <button
                disabled={busy || rawMovs == null || responsavelId == null}
                onClick={handleBaixarOSInteira}
                style={{
                  marginBottom: "8px",
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #a00",
                  background: "#fff5f5",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Dá baixa em todos os itens desta P.V."
              >
                Dar baixa na P.V.
              </button>
            </div>

            <div
              style={{
                width: "100%",
                overflowX: "auto",
                fontFamily: "Arial, sans-serif",
              }}
            >
              <div style={{ minWidth: 980 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: pvDetails.colWidths.join(" "),
                    fontWeight: 300,
                    padding: "10px 10px",
                    border: "1px solid #264777",
                    background: "#284a7c",
                    color: "#FFFFFFFF",
                    fontFamily: "Arial, sans-serif",
                  }}
                >
                  {pvDetails.headers.map((h, idx) => {
                    const centerCols = [0, 1, 3, 4, 6, 7];
                    const textAlign = centerCols.includes(idx)
                      ? "center"
                      : "left";
                    return (
                      <div key={h} style={{ textAlign }}>
                        {h}
                      </div>
                    );
                  })}
                </div>

                {pvDetails.tableRows.map((r, i) => {
                  const selected = i === selectedIndex;
                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedIndex(i)}
                      style={{
                        display: "grid",
                        gridTemplateColumns: pvDetails.colWidths.join(" "),
                        padding: "12px 10px",
                        borderBottom: "1px solid #eee",
                        background: selected ? "#e6f0ff" : "white",
                        cursor: "pointer",
                        userSelect: "none",
                        fontFamily: "Arial, sans-serif",
                      }}
                      title="Clique para selecionar"
                    >
                      {r.map((c, j) => {
                        const centerCols = [0, 1, 3, 4, 6, 7];
                        const textAlign = centerCols.includes(j)
                          ? "center"
                          : "left";
                        return (
                          <div
                            key={j}
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign,
                            }}
                            title={String(c)}
                          >
                            {c}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 8,
              }}
            >
              <button
                disabled={
                  busy ||
                  selectedIndex == null ||
                  rawMovs == null ||
                  responsavelId == null
                }
                onClick={handleRetornarItem}
                style={{
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #08a",
                  background: "#f0f8ff",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Retorna somente o item selecionado"
              >
                Retornar produto ao estoque
              </button>
              <button
                disabled={
                  busy ||
                  selectedIndex == null ||
                  rawMovs == null ||
                  responsavelId == null
                }
                onClick={handleBaixarItem}
                style={{
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #a00",
                  background: "#fff5f5",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Dá baixa somente no item selecionado"
              >
                Dar baixa no item
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SuprimentosStandby;
